import { ThemePalette } from 'mui.theme';
import { Typography, Box, LinearProgress } from '@mui/material';
import { getAssetsReplacementCount } from './utils';
import { truncateTo2dp } from 'utils/helpers/general';
import { formatNumberToShortScale } from 'views/Production/Hauler/component/utils';
import { useAppSelector } from 'store/hook';
import useAppTheme from 'hooks/useAppTheme';
import { useMemo } from 'react';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

const CustomProgress = ({
  value,
  total,
  unit,
  color,
}: {
  value: number;
  total: number;
  unit: string;
  color: string;
}) => {
  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const progressValue = useMemo(
    () => (Number(value || 0) / total) * 100,
    [value, total]
  );

  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Box sx={{ width: '100%', mr: 1 }}>
        <LinearProgress
          variant="determinate"
          value={progressValue}
          sx={{
            marginTop: '8px',
            height: 6,
            borderRadius: 4,
            bgcolor: 'rgba(255, 255, 255, 0.12)',
            '& .MuiLinearProgress-bar': {
              bgcolor: color,
              borderRadius: 4,
            },
          }}
        />
      </Box>
      <Box sx={{ minWidth: 45 }}>
        <Typography
          sx={{
            color: palette.textPrimary,
            fontSize: '10px',
          }}
        >
          {formatNumberToShortScale(value)}
          {unit}
        </Typography>
      </Box>
    </Box>
  );
};

const AssetDetails = ({
  data,
  selectedPart,
  selectedAsset,
  selectedYAxisCategory,
  sortType,
}: {
  data: any[];
  selectedPart: string[];
  selectedAsset: string[];
  selectedYAxisCategory: string;
  sortType: 'cost' | 'count';
}) => {
  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const assets = useAppSelector((state) => state.assetReducer.assets);

  // Pre-map assets by bumperNumber
  const assetMap = useMemo(() => {
    const map: Record<string, any> = {};
    assets.forEach((asset) => (map[asset.bumperNumber] = asset));
    return map;
  }, [assets]);

  const replacementData = useMemo(() => {
    return data.filter((item) => {
      const subCategory = item.sub_component_categories.replace(/^\[|\]$/g, '');
      const combinedCategory = `${item.components_category} - ${subCategory}`;
      if (selectedYAxisCategory) {
        return (
          item.actions_performed === 'REPLACE' &&
          selectedYAxisCategory === combinedCategory &&
          selectedAsset.includes(item.asset_id)
        );
      }
      return (
        item.actions_performed === 'REPLACE' &&
        selectedPart.includes(combinedCategory) &&
        selectedAsset.includes(item.asset_id)
      );
    });
  }, [data, selectedPart, selectedAsset, selectedYAxisCategory]);

  const assetsReplacementData = useMemo(() => {
    const processed = getAssetsReplacementCount(replacementData).sort(
      (a: any, b: any) => {
        const keyToSort = sortType === 'cost' ? 'totalCost' : 'count';
        return b[keyToSort] - a[keyToSort];
      }
    );
    return processed.map((item: any) => ({
      ...item,
      makeModel: assetMap[item.assetId]
        ? `${assetMap[item.assetId].make}-${assetMap[item.assetId].model}`
        : '',
    }));
  }, [replacementData, assetMap, sortType]);

  const totalReplacementCount = useMemo(() => {
    return assetsReplacementData.reduce(
      (sum: number, item) => sum + Number(item.count || 0),
      0
    );
  }, [assetsReplacementData]);

  const totalReplacementCost = useMemo(() => {
    return assetsReplacementData.reduce(
      (sum: number, item) => sum + Number(item.totalCost || 0),
      0
    );
  }, [assetsReplacementData]);

  return (
    <Box
      sx={{
        backgroundColor: enableDarkTheme
          ? palette.boxBackground
          : palette.toolBarBackground,
        padding: '20px 10px',
        borderRadius: '5px',
        color: palette.textPrimary,
        marginTop: 2,
        overflow: 'scroll',
        height: '428px',
      }}
      display={'flex'}
      flexDirection={'column'}
      gap={2}
    >
      <Typography sx={{ fontSize: '14px' }}>
        {`${selectedYAxisCategory || 'Parts'} replaced across Assets`}
      </Typography>

      <Box display={'flex'} flexDirection={'column'} gap={2}>
        {assetsReplacementData.map((asset: any, index) => (
          <Box
            key={asset?.assetId || `card-asset-${index}`}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              backgroundColor: enableDarkTheme
                ? palette.modalBackground
                : palette.boxBackground,
              p: 1,
              borderRadius: '5px',
            }}
          >
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between' }}
              flexDirection="column"
            >
              <Box
                display={'flex'}
                flexDirection={'row'}
                gap={1}
                alignItems={'center'}
              >
                <Typography sx={{ fontSize: '10px' }}>
                  {asset.assetId}
                </Typography>
                <Typography
                  sx={{
                    fontSize: '10px',
                    color: 'rgba(255,255,255,0.7)',
                  }}
                >
                  {asset.makeModel}
                </Typography>
              </Box>
              <Box sx={{ width: '100%' }}>
                <CustomProgress
                  color={palette.primary}
                  value={asset.count}
                  total={totalReplacementCount}
                  unit=""
                />
                <CustomProgress
                  color="#58BF6A"
                  value={Number(truncateTo2dp(asset.totalCost))}
                  total={totalReplacementCost}
                  unit="$"
                />
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default AssetDetails;
